
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { Button, ButtonGroup, DropdownMenu, UncontrolledTooltip, UncontrolledDropdown, DropdownToggle, DropdownItem, Nav, Navbar, NavItem, Modal, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next'
import { useSlate, ReactEditor } from 'slate-react'
import { Transforms, Editor, Path, Node, Element } from 'slate'
import { useMutation } from 'react-query'
import clsx from 'clsx';
import { toast } from 'components/Shared/Toast';
import styled from 'styled-components';

import { 
    // marks
    ToolbarButton,
    MARK_ITALIC,
    MARK_BOLD,
    MARK_UNDERLINE,
    MARK_STRIKETHROUGH,
    // align
    ELEMENT_ALIGN_LEFT,
    ELEMENT_ALIGN_CENTER,
    ELEMENT_ALIGN_RIGHT,
    ELEMENT_ALIGN_JUSTIFY,
    //list
    ELEMENT_UL,
    ELEMENT_OL,
    isCollapsed,
    isExpanded,
    getPreventDefaultHandler,
    someNode,
    findNode,
    toggleMark,
    getNodes
  } from '@udecode/slate-plugins';

import bootbox from 'components/Modals/Bootbox';
import { 
    serializeHTML, 
    insertImage, 
    insertRow, 
    insertColumn,
    deleteRow, 
    deleteColumn, 
    mergeCells,
    splitCells,
    scrollToNode, 
    MAX_TABLE_ROWS, 
    MAX_TABLE_COLS, 
    isMarkActive, 
    upsertAlign,
    insertList,
    isAlignActive
} from '../Canvas/TratoFormat'

import FontColorButton from './FontColorButton';
import BackgroundColorButton from './BackgroundColorButton';
import FontSizeButton from './FontSizeButton';
import LineHeightButton from './LineHeightButton';
import TableBackgroundColorButton from './TableBackgroundColorButton';
import { uploadFile, uploadFileV2 } from 'api/actions/file'
import { fetcher } from 'api/client'
import useFileUpload  from 'api/hooks/file/useFileUpload'
import { useProfileState, useProfileAbility } from 'context/profile'
import Whoami from 'actions/whoami-react'
import useWhoami from 'hooks/useWhoami';
import { useEditorState, useEditorActions } from '../editor-context'
import { downloadFile } from 'utils'
// const ELEMENT_ALIGN_JUSTIFY = 'justify'

function TratoFormatToolbar() {
    const { t } = useTranslation()

    const { currentElement, isFetching, isEditable } = useEditorState()
    const editor = useSlate();
    const { mutate: mutateUploadFile } = useMutation(({ file }) => fetcher(uploadFileV2(file)), {
        onSuccess: (data, { selection }) => {
            // const { selection } = editor;
            ReactEditor.focus(editor);
            if (selection) {
              Transforms.select(editor, selection);
            } 
            if (!selection && currentElement.editorLocation) {
                Transforms.select(editor, currentElement.editorLocation);
            }            
            insertImage(editor, { name: data.BaseFileName, url: data.url }); 
        }
    })

    const handleMouseDown = () => {
        const { selection } = editor;
        const collapsed = selection && isCollapsed(selection);
        console.log({selection, collapsed})
        if (!selection) return;
        // if (!collapsed) {
        //   Transforms.collapse(editor, { edge: 'end' });
        // }
        if (['document_header','document_footer'].includes(currentElement.type)) {
            const [node] = findNode(editor, {
                at: [currentElement.editorLocation.path[0]],
            })
            const text = Node.string(node)
            if (text.length) {
                return;
            }
        }
        
        const eleInput = document.createElement('input');
        eleInput.type = 'file';
        eleInput.style.display = 'none';
        eleInput.accept = 'image/*';
        eleInput.onchange = (e) => {
          const file = e.target.files[0];
          console.log({file})
          if (file) {
            mutateUploadFile({ file, selection });
          }
        };
        eleInput.click();
      };

      const isToolbarAlignEnabled = true || someNode(editor, { match: (n) => ['text', 'clause', 'statement',  'document_header', 'document_footer', 'ul', 'ol'].includes(n.type) })
      const isToolbarListDisabled = false; //someNode(editor, { match: (n) => ['table-wrap'].includes(n.type) })

    return (
        <ButtonGroup className="d-sm-inline-flex d-none" >
            <ToolbarMark  
                type={MARK_BOLD} 
                icon="bold"
                tooltip={t('editor.format.bold')}
                isDisabled={isFetching || !isEditable}
            />
            <ToolbarMark  
                type={MARK_ITALIC} 
                icon="italic"
                tooltip={t('editor.format.italic')}
                isDisabled={isFetching || !isEditable}
            />
            <ToolbarMark  
                type={MARK_UNDERLINE} 
                icon="underline" 
                tooltip={t('editor.format.underline')}
                isDisabled={isFetching || !isEditable}
            />
            <ToolbarMark  
                type={MARK_STRIKETHROUGH} 
                icon="strikethrough" 
                tooltip={t('editor.format.strike')} 
                isDisabled={isFetching || !isEditable}
            />
            <ToolbarDivider />
            <FontSizeButton isDisabled={isFetching || !isEditable} />
            <FontColorButton isDisabled={isFetching || !isEditable} />
            <BackgroundColorButton isDisabled={isFetching || !isEditable} />
            <LineHeightButton isDisabled={isFetching || !isEditable} />
            <ToolbarDivider />
            <ToolbarAlign 
                type="left" 
                icon="align-left" 
                tooltip={t('editor.format.align_left')} 
                isDisabled={isFetching || !isEditable || !isToolbarAlignEnabled}
            />
            <ToolbarAlign
                type="center" 
                icon="align-center" 
                tooltip={t('editor.format.center')}
                isDisabled={isFetching || !isEditable || !isToolbarAlignEnabled}
            />
            <ToolbarAlign
                type="right"
                icon="align-right" 
                tooltip={t('editor.format.align_right')} 
                isDisabled={isFetching || !isEditable || !isToolbarAlignEnabled}
            />
            <ToolbarAlign
                type="justify"
                icon="align-justify"
                tooltip={t('editor.format.justify')}
                isDisabled={isFetching || !isEditable || !isToolbarAlignEnabled}
            />
            <ToolbarDivider />
            <ToolbarList 
                type={ELEMENT_UL}
                icon="list-ul"
                tooltip={t('editor.format.unorder_list')}
                isDisabled={isFetching || !isEditable || isToolbarListDisabled}
            />
            
            <ToolbarList 
                type={ELEMENT_OL}
                icon="list-ol"
                tooltip={t('editor.format.order_list')}
                isDisabled={isFetching || !isEditable || isToolbarListDisabled}
            />
            <ToolbarDivider />
            <ToolbarItem 
                className="editor-toolbar-item editor-toolbar-item-sm" 
                icon="image"
                action="insert-image"
                tooltip={t('editor.format.image')}
                onClick={handleMouseDown}
                isDisabled={isFetching || !isEditable}
            />
            <ToolbarTable isDisabled={isFetching || !isEditable} />
        </ButtonGroup>
    )
}

function ToolbarMark({ icon , tooltip, type, isDisabled, clear })  {
    const editor = useSlate();


    return <ToolbarItem 
        icon={icon}
        active={isMarkActive(editor, type)}
        action={type}
        tooltip={tooltip}
        isDisabled={isDisabled}
        onClick={getPreventDefaultHandler(toggleMark, editor, type, clear)}
    />
}


function ToolbarAlign({ icon , tooltip, type = 'left', isDisabled, ...rest }) {
    const editor = useSlate();
    const unwrapTypes = [
        ELEMENT_ALIGN_LEFT,
        ELEMENT_ALIGN_CENTER,
        ELEMENT_ALIGN_RIGHT,
        ELEMENT_ALIGN_JUSTIFY,
      ]

    return <ToolbarItem 
        icon={icon}
        action={type}
        active={isAlignActive(editor, { type })}
        tooltip={tooltip}
        isDisabled={isDisabled}
        onClick={getPreventDefaultHandler(upsertAlign, editor, {
            type,
            unwrapTypes,
        })}
    />
}
function ToolbarList({ icon , tooltip, type, isDisabled, ...rest }) {
    const editor = useSlate();
  
    return <ToolbarItem 
        icon={icon}
        action={type}
        tooltip={tooltip}
        isDisabled={isDisabled}
        onClick={getPreventDefaultHandler(insertList, editor, type, {
            ...rest,
            typeList: type,
        })}
    />
}

export const isBlockActive = (editor, format) => {
    try {
        const [match] = Editor.nodes(editor, {
            match: (n) => n.type === format,
          });
          return !!match;
    } catch (error) {
        return false
    }
    
};

function ToolbarTable({ isDisabled }) {
    const editor = useSlate();
    const active = isBlockActive(editor, 'table-wrap');
    const tableEntry = findNode(editor, { match: (n) => n.type === 'table'})
    const [tableNode] = tableEntry || [];
    const tableRows =  [...Editor.nodes(editor, { match: (n) => n.type === 'table-row' })]
    const tableCells =  [...Editor.nodes(editor, { match: (n) => n.type === 'table-cell' })]
    const splitableCells = [...Editor.nodes(editor, { match: (n) => n.type === 'table-cell' && n.colspan > 1 })];
    const canMergeCells = tableCells.length > 1 && tableRows.length < 2
    const canSplitCells = splitableCells.length;

    if (!active) {
        return null;
    }

    return (
        <>
            <ToolbarDivider />
            <TableBackgroundColorButton  isDisabled={isDisabled} />
            <ToolbarItem 
                image={'/images/editor/icons/table/insert-row-up.svg'}
                onClick={() => insertRow(editor, 'top')}
                isDisabled={isDisabled || tableNode?.row >= MAX_TABLE_ROWS}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/insert-row-down.svg'}
                onClick={() => insertRow(editor, 'bottom')}
                isDisabled={isDisabled || tableNode?.row >= MAX_TABLE_ROWS}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/insert-col-left.svg'}
                onClick={() => insertColumn(editor, 'left')}
                isDisabled={isDisabled || tableNode?.col >= MAX_TABLE_COLS}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/insert-col-right.svg'}
                onClick={() => insertColumn(editor, 'right')}
                isDisabled={isDisabled || tableNode?.col >= MAX_TABLE_COLS}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/del-row.svg'}
                onClick={() => deleteRow(editor)}
                isDisabled={isDisabled}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/del-col.svg'}
                onClick={() => deleteColumn(editor)}
                isDisabled={isDisabled}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/merge-cells.svg'}
                onClick={() => mergeCells(editor)}
                isDisabled={isDisabled || !canMergeCells}
            />
            <ToolbarItem 
                image={'/images/editor/icons/table/split-cell.svg'}
                onClick={() => splitCells(editor)}
                isDisabled={isDisabled || !canSplitCells}
            />
        </>
    )
}




const ToolbarItem = React.forwardRef(({ className, active, icon, image, action, tooltip, onClick, children, isDisabled }, ref) => {
    const { isFetching } = useEditorState()
    const tooltipId = `ToolbarItemTooltip-${action}`

    return (
        
        <Button size="sm" color="light" disabled={isFetching || isDisabled} innerRef={ref} className={clsx(className, isFetching && 'b-disabled', isDisabled && 'b-disabled not-allowed opacity-50', active && 'text-primary')} data-action={action} onMouseDown={onClick} id={tooltipId}>
                { tooltip && <UncontrolledTooltip placement="bottom" target={tooltipId}>{tooltip}</UncontrolledTooltip> }
                {
                    image ? <img src={image} style={{width: 18, height: 18, opacity: 0.6}} />
                    : <i className={`fas fa-${icon}`} /> 
                }
                {' '}{children}
            
        </Button>
        
    )
})

const ToolbarDivider = styled.li`
    border-left: 1px solid #dadada;
    margin: 5px 5px 0px 5px;
    height: 20px;
`

function WordonlineModal({ isWordonlineOpen }) {
    const { t } = useTranslation();
    return (
        <Modal isOpen={isWordonlineOpen} centered>
            <ModalBody className="text-center font-weight-bold">
                <img src="/images/office365.png" alt="office" width="350px"/>
                <p>
                    {t('editor.action.edit_in_wordonline')}
                </p>
            </ModalBody>
        </Modal>
    )
}

export function ActionsToolBar() {
    const { t } = useTranslation();
    const { document: doc, elements, documentType, mode, isFetching, isEditable } = useEditorState();
    const { finalizeDocument, changePdf, quickVobo, vote, appproveNegotiation, continueWorkflow, approveContractSign, downloadWord, signWordOnline, checkVobo, reown, takeNegotiationTurn, giveVobo, saveDocument, uploadWordVersion, sendSpecialValidation, sendToSign, initWorkflow, setIsSidebarOpen } = useEditorActions();
    const { shouldShow } = useToolbarActionChecker(doc, mode, isFetching);
    const [isUploadingPdf, setIsUploadingPdf] = useState(false);
    const saveButtonRef = useRef(false);
    const [isWordonlineOpen, setIsWordOnlineOpen] = useState(false);
    const { chooseFileAndUploadFile } = useFileUpload();
    const { whoami } = useWhoami(doc);
    const profileAbility = useProfileAbility()

    const canOpenInWordOnline = profileAbility.can('OPEN_IN_WORD_ONLINE'); // can OPEN_IN_WORD_ONLINE
    const canNegotiationWord = profileAbility.can('NEGOTIATION_WORD'); // can NEGOTIATION_WORD
    let nextStep = '';
    if (doc.workflowNextStepPopulated && doc.workflowNextStepPopulated.type === 'approval') {
      nextStep = `${doc.workflowNextStepPopulated?.user?.givenName || ''} ${doc.workflowNextStepPopulated?.user?.familyName || ''}`;
    }

    const handleChangePdf = async () => {
        const res = await bootbox.confirm(t('editor.action.import_pdf_confirm'))
        if (!res) return;

        chooseFileAndUploadFile('', {
            accept: 'application/pdf',
            onMutate: () => { setIsUploadingPdf(true)},
            onSuccess: (data, variables) => { 
                changePdf({ filename: variables.file.name, url: data.url }, {
                    onSuccess: () => { setIsUploadingPdf(false) }
                })
            }
        })
    }

    const handleQuickApprove = (justvobo) => async () => {
        const payload = { status: 'approved', approveContract: !justvobo };
        
        const resp = await bootbox.confirm(t('editor.collaboration.vobo_quick_confirm'));
        
        if (!resp) return;

        quickVobo(payload)
    }

    const handleVote = (status) => () => vote({ status });

    const handleApproveNegotiation = () => appproveNegotiation();

    const handleSpecialValidation = (status) => async (e) => {
        if (status === 'approve') {
            const resp = await bootbox.confirm('¿Deseas aprobar este documento?')
            if (!resp) return 
              
            sendSpecialValidation({ approval: true })
        } else {
            const resp = await bootbox.confirm(t('editor.canvas.toolbar.reject_document'))
            if (!resp) return 

            const reason = bootbox.prompt(t('editor.canvas.toolbar.reject_reason'))
            if (!reason) {
                toast.error(t('editor.canvas.toolbar.why_you_reject_contract'));
                return;
            }

            sendSpecialValidation({ approval: true, reason }) 
        }
    }

    const handleContinueWorkflow = () => continueWorkflow() 

    const handleSendDocumentVoBo = (status) => async () => {
        const action = status ? 'approved' : 'rejected';

        if (action === 'rejected') {
          const reason = await bootbox.prompt(t('editor.action.vobo_reject_reason'));
          if (reason) {
            giveVobo({ status: action, reason })
          } else if (reason === '') {
            await bootbox.alert(t('editor.action.vobo_reject_reason_mandatory'));
            handleSendDocumentVoBo(false)()
          }
        } else {
          const resp = await bootbox.confirm(t('editor.action.vobo_send_confirm'));
          if (resp) giveVobo({ status: action })
        }
    }

    const handleApproveContractSign = (approve, group) => () => approveContractSign({ approve, group })

    const handleOpenWordOnline = () => {
        setIsWordOnlineOpen(true);
        const wordonlineFrame = window.open(`${window.location.origin}/wordonline/${doc._id}`, '_blank');
        const interval = setInterval(checkFrame, 1000);
        function checkFrame() {
          if (wordonlineFrame.closed) {
            setTimeout(() => {
              clearInterval(interval);
              setIsWordOnlineOpen(false);
            }, 1500);
          }
        }
    }

    const handleUploadWordOnline = async (e)  => {
        chooseFileAndUploadFile({ Key: doc.wordonline.key }, {
            accept: '.doc,.docx',
            onSuccess: (data, variables) => { 
                uploadWordVersion({ ...data, filename: variables.file.name, url: data.url, clearStatus: false })
            }
        })
    }

    const handleDownloadWordOnline = async () => downloadWord()

    const handleSignWordOnline = async (confirmed) => {
        if (!confirmed) {
          const res = await bootbox.confirm(t('editor.canvas.toolbar.generate_version_to_sign'));
          if (!res) return;
        }

        signWordOnline()
    }

    const handleSendToStarter = () => {
        // app.request('modals').destroyAll();
        // app.request('modals').show(new SendStarterTemplate());
    }

    const handleSave = async (obj, confirm) => {
        const hasVobo = await checkVobo(doc);
     
        if (!confirm && typeof confirm === 'undefined' && !hasVobo) {
            return
        }

        saveDocument({}, { updateElements: true })
    }

    const handleToggleSidebar = () => {
        setIsSidebarOpen(true)
    }

    const handleSendDocumentNew = (action) => async () => {
            if (documentType === 'word' && action === 'sign') {
            //   await self.saveDocument(null, true);
              const hasNegotiationWord = canNegotiationWord ?? false;
              
                let buttons = {
                  cancel: {
                    label: t('editor.action.cancel'),
                  },
                  sign: {
                    label: t('editor.action.send_sign'),
                    color: 'primary',
                    callback: async () => {
                        const res = await bootbox.confirm(t('editor.wordonline.errors.convert_to_pdf_first'));
                        if (!res) return;
                        handleSignWordOnline(true);
                    },
                  },
                }
        
                if (hasNegotiationWord) {
                  buttons = {
                    cancel: buttons.cancel,
                    negotiate: {
                      label: t('editor.action.negotiate'),
                      color: 'secondary',
                      callback: () =>{
                        sendToSign({ action: 'negotiate-word' });
                      },
                    },
                    sign: buttons.sign,
                  }
                }
        
               return bootbox.dialog({
                  message: t('editor.wordonline.negotiateOrSendSign'),
                  buttons,
                })
            }
        
            if (whoami.hasVobo()) {

                sendToSign({ action, preventSave: true });
            } else {
                sendToSign({ action });
            //   const oldStatus = self.model.get('status');
            //   await self.saveDocument(null, true);
            //   const newStatus = self.model.get('status');
        
            //   if (oldStatus === 'PRE_APPROVED' && newStatus !== 'PRE_APPROVED') {
            //     bootbox.alert(t('editor.action.contract_no_longer_approved'));
            //   } else {
            //     sendToSign({ action });
            //   }
            }
        
    }

    const handleInitWorkflow = async () => initWorkflow()

    const handleAskForDelegation = async () => {
        const resp = await bootbox.confirm(t('editor.canvas.toolbar.send_notification_to_owner'));
        if (!resp) return;
        
        reown()
    }

    const handleTakeNegotiationTurn = async () => takeNegotiationTurn()

    const handleChangeWord = async () => {
        handleUploadWordOnline()
        // const whoami = app.request('editor:whoami');
        // const target = e.currentTarget;
        // const self = this;
        // const contract = self.model;
        // const changelog = contract.get('changelog');
        // const file = target.files[0];
        // const reader = new FileReader();
        // reader.onload = (readerEvent) => {
        // const data = readerEvent.target.result;
        // const model = new AttachmentModel({
        //     name: file.name,
        //     data,
        //     contract: contract.get('_id')
        // });

        // if (contract.get('importedWord'))
        //     changelog.push({ user: whoami.name, url: contract.get('importedWord'), type: 'version' })

        // const eventModel = new Model();
        // eventModel.url = `/timeline/add/event/document-replace/${contract.get('_id')}`;
        // eventModel.set({ properties: { filename: file.name, url: model.get('url') } });
        // eventModel.save().then(function () {
        //     model.save().then(() => {
        //     model.unset('data');
        //     self.saveDocument({ name: file.name, importedWord: model.get('url'), prefilled: false, elements: [], changelog });
        //     });
        // });

        // };
        // reader.readAsDataURL(file);
    }

    return (
        <div className="d-flex">
            <ShouldShow show={shouldShow('voting')}>
                <ToolbarDivider />
                <ButtonGroup>
                    <UncontrolledDropdown>
                        <DropdownToggle lassName="text-dark" disabled={isFetching} nav>
                            <Button color="primary" size="sm" className="dropdown-toggle send-btn" data-toggle="dropdown">
                                Votar <span className="caret"></span>
                            </Button>
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem data-action="vote-for" onClick={handleVote('for')}>
                                <i className="fas fa-thumbs-up text-success"></i>{t('A favor')}
                            </DropdownItem>
                            <DropdownItem data-action="vote-against" onClick={handleVote('against')}>
                                <i className="fas fa-thumbs-down text-danger"></i>{t('En contra')}
                            </DropdownItem>  
                            <DropdownItem data-action="vote-abstention" onClick={handleVote('abstention')}>
                                <i className="fas fa-hand-stop-o text-default"></i>{t('Me abstengo')}
                            </DropdownItem>     
                        </DropdownMenu>                     
                    </UncontrolledDropdown>
                </ButtonGroup> 
            </ShouldShow>
            <ShouldShow show={shouldShow('view-negotiation')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="eye"
                    action="view-sign-view"
                    onClick={() =>  window.open(`/sign/${doc._id}`)}
                >
                    {t('editor.action.view_negotiation')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('approve-negotiation')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="check"
                    action="approve-negotiation"
                    onClick={handleApproveNegotiation}
                >
                    {t('contracts.negotiation.approve_negotiation')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('validacion-datos-fijos')}>
                <ToolbarDivider />
                <UncontrolledDropdown>
                    <DropdownToggle disabled={isFetching} caret nav>
                        {t('contracts.negotiation.validation')}
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem data-action="special" onClick={handleSpecialValidation('approve')}>
                            
                                <i className="fas fa-thumbs-up"></i>
                                {t('editor.action.approve_document')}
                            
                        </DropdownItem>
                        <DropdownItem data-action="special" onClick={handleSpecialValidation('reject')}>
                            
                                <i className="fas fa-thumbs-down"></i>
                                {t('editor.action.reject_document')}

                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </ShouldShow>
            <ShouldShow show={shouldShow('next-workflow-step')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="forward"
                    action="next-workflow-step"
                    tooltip={`${t('contracts.collaborators.approval')} (${nextStep})`}
                    onClick={handleContinueWorkflow}
                >
                    {t('workflows.next_step')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('quick-approve')}>
                <ToolbarItem 
                    icon="thumbs-up"
                    action="quick-approve"
                    onClick={handleQuickApprove()}
                >
                    {t('editor.action.quick_approve')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('quick-vobo')}>
                <ToolbarItem 
                    icon="thumbs-up"
                    action="quick-vobo"
                    onClick={handleQuickApprove(true)}
                >
                    {t('editor.action.quick_vobo')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('vobo')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="times"
                    action="send-vobo-reject"
                    onClick={handleSendDocumentVoBo(false)}
                >
                    {t('editor.action.reject_document')}
                </ToolbarItem>
                <ToolbarItem 
                    icon="check"
                    action="send-vobo-approve"
                    onClick={handleSendDocumentVoBo(true)}
                >
                    {t('editor.action.approve_document')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('validate_contract')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="times"
                    action="validate-reject"
                    onClick={handleApproveContractSign(false)}
                >
                    {t('editor.action.reject_document')}
                </ToolbarItem>
                <ToolbarItem 
                    icon="check"
                    action="validate-approve"
                    onClick={handleApproveContractSign(true)}
                >
                    {t('editor.action.approve_document')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('validate_contract_group')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="times"
                    action="validate-reject-group"
                    onClick={handleApproveContractSign(false, true)}
                >
                    {t('editor.action.reject_document')}
                </ToolbarItem>
                <ToolbarItem 
                    icon="check"
                    action="validate-approve-group"
                    onClick={handleApproveContractSign(true, true)}
                >
                    {t('editor.action.approve_document')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('wordonline')}>
                <ToolbarDivider />
                <UncontrolledDropdown>
                    <DropdownToggle className="text-dark" disabled={isFetching} nav caret>
                        {t('editor.action.word_online_options')}
                        </DropdownToggle>
                    <DropdownMenu>
                        <input type="file" className="d-none" name="import-word-input" />
                        {canOpenInWordOnline ?
                            <DropdownItem data-action="open-word-online" onClick={handleOpenWordOnline}> 
                                    <img className="mr-2" src="/images/ui/word-online.png" />
                                    {t('editor.action.edit_word_online')}
                            </DropdownItem>
                        :
                            <DropdownItem
                                style={{'cursor': 'not-allowed'}}
                                title={t('wordList.plans.availableE')}
                            >
                                    <i
                                        className="fas fa-lock not-allowed mr-2"
                                        style={{'cursor': 'not-allowed'}}
                                    />
                                    {t('editor.action.edit_word_online')}
                            </DropdownItem>
                        }
                        <DropdownItem data-action="upload-word-online" onClick={handleUploadWordOnline}>
                                <i className="fas fa-upload mr-2"></i>
                                {t('editor.action.upload_new_version')}
                        </DropdownItem>
                        <DropdownItem data-action="download-word-online" onClick={handleDownloadWordOnline}>
                                <i className="fas fa-download mr-2"></i>
                                {t('editor.action.download_version')}
                        </DropdownItem>
                        {shouldShow('canConvertToSignVersion') &&
                            <DropdownItem data-action="sign-word-online" onClick={handleSignWordOnline}>
                                    <i className="fas fa-pen mr-2"></i>
                                    {t('editor.action.sign_version')}
                            </DropdownItem>
                        }
                    </DropdownMenu>
                </UncontrolledDropdown>
            </ShouldShow>
            <ShouldShow show={shouldShow('send-to-starter')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="paper-plane"
                    action="send-to-starter"
                    onClick={handleSendToStarter}
                >
                    {t('Enviar a Starter')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('view-sign-view')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="eye"
                    action="view-sign-view"
                    onClick={() =>  window.open(`/sign/${doc._id}`)}
                >
                    {t('editor.action.view_signing')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('change-pdf')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="file-pdf"
                    action="importpdf"
                    onClick={handleChangePdf}
                >
                    {isUploadingPdf ? t('Subiendo...') : t('editor.action.change_pdf')}
                </ToolbarItem>
                <input type="file" className="d-none" name="import-pdf-input" />
            </ShouldShow>
            <ShouldShow show={shouldShow('save')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="save"
                    action="save"
                    onClick={handleSave}
                    ref={saveButtonRef}
                    tooltip={t('editor.action.save')}
                    // isDisabled={!isEditable}
                >
                    {/* {t('editor.action.save')} */}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={ shouldShow('send-sign')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="paper-plane"
                    action="send"
                    onClick={handleSendDocumentNew('sign')}
                    ref={saveButtonRef}
                    tooltip={t('editor.action.send')}
                    isDisabled={isFetching}
                />
                {/* <Button 
                    id="send-sign-button" 
                    className={clsx("ml-1", isFetching && "b-disabled")}
                    color="light" 
                    size="sm" 
                    data-action="send" //{{#is signatureType 'fad'}}-fad{{/is}}
                    onClick={handleSendDocumentNew('sign')}
                    disabled={isFetching}
                >
                    <i className="fas fa-paper-plane" /> {t('editor.action.send')}
                </Button> */}
            </ShouldShow>
            <ShouldShow show={shouldShow('init-workflow')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="play"
                    action="init-workflow"
                    onClick={handleInitWorkflow}
                >
                    {t('workflow.actions.init')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('finalize')}>
                <ToolbarDivider />
                <Button 
                    id="finalize" 
                    color="success" 
                    className={clsx(isFetching && 'b-disabled')}
                    size="sm" 
                    data-action="finalize" 
                    onClick={finalizeDocument}
                    isDisabled={isFetching}
                >
                    <i className="fas fa-lock" /> {t('editor.action.finalize')}
                </Button>
            </ShouldShow>
            <ShouldShow show={shouldShow('ask-delegation')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="play"
                    action="ask-delegation"
                    onClick={handleAskForDelegation}
                >
                    {t('editor.action.request_document')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('take-negotiation-turn')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="hand-stop-o"
                    action="take-negotiation-turn"
                    onClick={handleTakeNegotiationTurn}
                >
                    {t('contracts.negotiation.take')}
                </ToolbarItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('upload-negotiation-version')}>
                <ToolbarDivider />
                <ToolbarItem 
                    icon="upload"
                    action="upload-word-online"
                    onClick={handleUploadWordOnline}
                >
                    {t('editor.action.upload_new_version')}
                </ToolbarItem>
            </ShouldShow>
            <ToolbarDivider className="d-xl-none" />
            <ToolbarItem 
                className="d-xl-none"
                icon="bars"
                action="open-sidebar"
                onClick={handleToggleSidebar}
            />
            <WordonlineModal isWordonlineOpen={isWordonlineOpen} />
            
        </div>
    )
}

function PdfToolbar () {
    const { t } = useTranslation()
    const { document: doc, currentPage, mode } = useEditorState();
    const { addSignature } = useEditorActions();
    const { shouldShow } = useToolbarActionChecker(doc, mode);

    const handleAddSignature = (participant) => () => {
        addSignature({
            participantId: participant._id,
            page: currentPage ?? 1,
        })
    }

    console.log({doc})
        

    return (
        <Nav className="d-flex align-items-center">
            <ShouldShow show={shouldShow('download-nom')}>
                <NavItem hidden={!doc.nom151}>
                    <ToolbarItem 
                        className="d-none d-md-block"
                        action="download"
                        onClick={downloadFile({ url: doc.nom151, name: `${doc.name}-nom151.pdf`, ext: 'pdf' })}
                    >
                        {t('sign.contract.tab_certificate')}
                    </ToolbarItem>
                </NavItem>
                <NavItem className="ml-1" hidden={!doc.nom151Bin}>
                    <ToolbarItem 
                        className="d-none d-md-block"
                        action="download"
                        onClick={downloadFile({ url: doc.nom151Bin, name: `${doc.name}-nom151.der`, ext: 'der' })}
                    >
                        {t('sign.contract.tab_downloadcert')}
                    </ToolbarItem>
                </NavItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('download-rfc3161')}>
                <NavItem className="ml-1" hidden={!doc.rfc3161}>
                    <ToolbarItem 
                        className="d-none d-md-block"
                        action="download"
                        onClick={downloadFile({ url: doc.rfc3161, name: `${doc.name}-rfc3161.pdf` })}
                    >
                        {t('sign.contract.tab_rfc3161',  { context: doc.jurisdiction })}
                    </ToolbarItem>
                </NavItem>
                <NavItem className="ml-1" hidden={!doc.rfc3161Bin}>
                    <ToolbarItem 
                        className="d-none d-md-block"
                        action="download"
                        onClick={downloadFile({ url: doc.rfc3161Bin, name: `${doc.name}-rfc3161.tsr` })}
                    >
                        {t('sign.contract.tab_downloadrfc3161',  { context: doc.jurisdiction })}
                    </ToolbarItem>
                </NavItem>
            </ShouldShow>
            <ShouldShow show={shouldShow('insert-signatures')}>
                <UncontrolledDropdown className="d-none d-md-block">
                    <DropdownToggle className="text-primary" nav caret>
                        {t('editor.action.insert_signature')}
                    </DropdownToggle>
                    <DropdownMenu>
                        {doc.participants.filter(p => !p.isNotSigner).map(participant => (
                            <DropdownItem key={participant._id} data-id={participant._id} onClick={handleAddSignature(participant)}>
                                    {participant.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </UncontrolledDropdown>
            </ShouldShow>
            <ShouldShow show={shouldShow('download-pdf')}>
                <NavItem className="pointer" >
                    <ToolbarItem 
                        action="download"
                        onClick={downloadFile({ url: doc.importedPdf, name: doc.name, ext: 'pdf' })}
                    >
                        {t('editor.action.download_document')}
                    </ToolbarItem>
                </NavItem>
            </ShouldShow>
        </Nav>
    )
}

function Toolbar() {
    const { document: doc, documentType, mode } = useEditorState();
    const { shouldShow } = useToolbarActionChecker(doc, mode);
    
    
    return (
        <Nav className="bg-light h-100 d-flex justify-content-between align-items-center px-2">
            <div>
                { documentType === 'trato' && shouldShow('format-bar') && <TratoFormatToolbar /> }
                { documentType === 'pdf' && <PdfToolbar /> }
            </div>
            <div>
                <ActionsToolBar />  
            </div>
            
        </Nav>
    )
}

export default Toolbar;

function ShouldShow({show, children}) {
    if (!show) return null;

    return (
        <>
            {children}
        </>
    )
}


function useToolbarActionChecker(contract, editorMode) {
    const { profile, roles } = useProfileState();
    const whoami = new Whoami(contract, profile.email, profile._id, roles, profile.organization, profile.groups);
    
    const result =  {
        'next-workflow-step': () => {
          if (editorMode === 'frontdesk') return false;
          return whoami.canForwardWorkflow()
        },
        'format-bar': () => {
          if (whoami.someoneSigned()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isPdf()) return false;
          if (whoami.isWordOnline()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.isWatcher()) return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (editorMode === 'frontdesk') return false;
          return true;
        },
        'send-sign': () => {
          if (editorMode === 'frontdesk') return false;
          if (whoami.isApproved()) return true;
          if (whoami.hasWorkflow() && !whoami.isPreApproved()) return false;
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (editorMode === 'template') return false;
          if (whoami.canBeSigned()) return true;
        },
        'insert-signatures': () => {
          if (!whoami.isOwner()) return false;
          if (editorMode !== 'template' && (!whoami.isDraft() && !whoami.isApproved())) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.hasNoSignature()) return false;
          if (whoami.isCertificateSignature()) return false;
          if (!whoami.isPdf()) return false;
          if (whoami.isSigned()) return false;
          if (editorMode === 'frontdesk') return false;
          return !contract.showSignaturesPdf;
        },
        'download-pdf': () => {
          if (whoami.isCanceled()) return false;
          if (editorMode === 'frontdesk') return false;
          if (whoami.isPdf()) return true;
        },
        'download-nom': () => {
          if (whoami.hasNom151()) return true;
        },
        'download-rfc3161': () => {
          return !!contract.rfc3161Bin && ['pr', 'co'].includes(contract.jurisdiction);
        },
        'show-insert-signatures': () => {
          if (!whoami.isOwner()) return false;
          if (editorMode !== 'template' && (!whoami.isDraft() && !whoami.isApproved())) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.hasNoSignature()) return false;
          if (!whoami.isPdf()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.isCertificateSignature()) return false;
          if (editorMode === 'frontdesk') return false;
          return contract.showSignaturesPdf;
        },
        'validacion-datos-fijos': () => {
          if (whoami.isCanceled()) return false;
          if (whoami.isSigned()) return false;
          if (editorMode === 'template') return false;
          if (whoami.roleCan('VALIDACION_DATOS_FIJOS'));
        },
        'remove-quick-approve': () => {
          if (whoami.isWordOnline()) return false;
          if (whoami.currentUserHasGiveVobo()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isSigned()) return false;
          if (editorMode === 'template') return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (whoami.hasToValidate()) return false;
          if (whoami.myGroupHasToValidate()) return false;
          return whoami.roleCan('LAWYER_APPROVE');
        },
        'quick-approve': () => {
          if (whoami.isWordOnline()) return false;
          if (whoami.isApproved()) return false;
          if (whoami.currentUserHasGiveVobo()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isSigned()) return false;
          if (editorMode === 'template') return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (whoami.hasToValidate()) return false;
          if (whoami.myGroupCanTakeVobo()) return false;
          if (whoami.myGroupHasToValidate()) return false;
          return whoami.roleCan('LAWYER_APPROVE');
        },
        'remove-quick-vobo': () => {
          if (whoami.isWordOnline()) return false;
          if (whoami.currentUserHasGiveVobo()) return false;
          if (whoami.isCanceled()) return false;
        //   if (whoami.isSigned()) return false;
          if (editorMode === 'template') return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (whoami.hasToValidate()) return false;
          if (whoami.myGroupHasToValidate()) return false;
          return whoami.roleCan('LAWYER_VOBO');
        },
        'quick-vobo': () => {
          if (whoami.isWordOnline()) return false;
          if (whoami.isBeingNegotiated()) return false;
          if (whoami.currentUserHasGiveVobo()) return false;
          if (whoami.isCanceled()) return false;
        //   if (whoami.isSigned()) return false;
          if (editorMode === 'template') return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (whoami.hasToValidate()) return false;
          if (whoami.myGroupCanTakeVobo()) return false;
          if (whoami.myGroupHasToValidate()) return false;
          return whoami.roleCan('LAWYER_VOBO');
        },
        'change-pdf': () => {
          if (!whoami.isOwner()) return false;
          if (whoami.isApproved()) return false;
          if (whoami.someoneApproved()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.isFinalized()) return false;
          if (whoami.someoneSigned()) return false;
          if (whoami.isWordOnline() && !whoami.isPdf()) return false;
          if (editorMode === 'frontdesk') return false;
          if (whoami.isPdf()) return true;
        },
        'canConvertToSignVersion': () => {
          if (editorMode === 'frontdesk') return false;
          if (whoami.isBeingNegotiated()) return false;
        },
        'wordonline': () => {
          if (editorMode === 'frontdesk') return false;
          if (editorMode === 'template') return false;
          if (whoami.isBeingNegotiated() && !whoami.someoneHasNegotiationTurn() && whoami.isWordOnline() && (whoami.isNegotiator() || whoami.isOwner())) return true
          if (whoami.isBeingNegotiated() && !whoami.isNegotiator()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isPdf()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.isWordOnline()) return true;
        },
        'save': () => {
          if (whoami.someoneSigned()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isPartnerClient()) return true;
          if (!whoami.isOwner()) return false;
          if (!whoami.isEditable()) return false;
          if (whoami.isWatcher()) return false;
          if (whoami.isBeingNegotiated()) return false;
          if (editorMode === 'frontdesk') return false;
          return true;
        },
        'send-to-starter': () => {
          if (whoami.isCanceled()) return false;
          if (!whoami.isEditable()) return false;
          if (whoami.isPartnerClient()) return false;
          if (whoami.isWatcher()) return false;
          if (!whoami.isTemplate()) return false;
          if (editorMode === 'frontdesk') return false;
          if (!whoami.roleCan('SEND_TO_STARTER')) return false;
          return true;
        },
        'init-workflow': () => {
          if (!whoami.isOwner()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.isFinalized()) return false;
          if (whoami.isCanceled()) return false;
          if (editorMode === 'template') return false;
          if (editorMode === 'frontdesk') return false;
          if (whoami.isPreApproved()) return false;
          if (whoami.isSentToDownload()) return false;
          if (whoami.isSigningOrCompleting() || whoami.isInNegotiation()) return false;
          if (whoami.hasWorkflow() && !whoami.hasWorkflowInitiated()) return true;
        },
        'finalize': () => {
          if (whoami.isCanceled()) return false;
          if (editorMode === 'template') return false;
          if (editorMode === 'frontdesk') return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isSigned()) return true;
          //if (whoami.isWordOnline()) return true;
          if (!whoami.canBeFinalized()) return false;
        },
        'vobo': () => {
          if (whoami.isCanceled()) return false;
          if (editorMode === 'template') return false;
          if (whoami.currentUserHasToVobo()) return true;
        },
        'ask-delegation': () => {
          if (whoami.isCanceled()) return false;
          if (editorMode === 'template') return false;
          if (editorMode === 'frontdesk') return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (!whoami.isOwner() && whoami.isWatcher()) return true;
        },
        'view-negotiation': () => {
          if (editorMode === 'frontdesk') return false;
          if (whoami.isInNegotiation()) return true;
        },
        'view-sign-view': () => {
          if (editorMode === 'frontdesk') return false;
          if (whoami.isSigningOrCompleting() && (whoami.hasToSign() || whoami.hasToComplete())) return true;
        },
        'voting': () => {
            if (editorMode === 'frontdesk') return false;
            return whoami.hasToVote();
        },
        'validate_contract': () => { 
            if (editorMode === 'frontdesk') return false;
            return whoami.hasToValidate();
        },
        'validate_contract_group': () => {
            if (editorMode === 'frontdesk') return false;
            return whoami.myGroupHasToValidate();
        },
        'approve-negotiation': () => {
            if (editorMode === 'frontdesk') return false;
            return whoami.canApproveNegotiation();
        },
        'upload-negotiation-version': () => {
          if (editorMode === 'frontdesk') return false;
          return whoami.isBeingNegotiated() && whoami.iHaveNegotiationTurn() && whoami.isWordOnline();
        },
        'take-negotiation-turn': () => {
          if (editorMode === 'frontdesk') return false;
          if (!whoami.isWordOnline()) return false;
          const participant = whoami.isNegotiator();
          return whoami.isBeingNegotiated() && !whoami.someoneHasNegotiationTurn() && participant && ['PENDING_NEGOTIATION'].includes(participant.status)
        },
        'default': () => false,
      };

    function shouldShow(action) {
        const actionFn = result[action];
        
        if (!actionFn) return false;

        return result[action]();
    }

    function shouldHide(action) {
        const actionFn = result[action];
        if (!actionFn) return false;

        return !result[action]();
    }

    return { shouldShow, shouldHide }
}